import { useCallback } from 'react'

import type { DragEndEvent } from '@dnd-kit/core'
import { Temporal, toTemporalInstant } from '@js-temporal/polyfill'
import { useApolloClient } from '@apollo/client/react'

import { toast } from '@buffer-mono/popcorn'

import { CURRENT_TIME_ZONE } from '~publish/helpers/dateFormatters'
import { postStatusByTab } from '~publish/helpers/post'
import { format, getZonedNow, isBefore } from '~publish/helpers/temporal'
import { GetPostList } from '~publish/pages/AllChannels/PostList/usePaginatedPostList'
import type { CalendarPostCard_PostData } from '~publish/pages/Calendar/fragments/CalendarPostCard_Post'
import { useDropPostMutation } from '~publish/pages/Channel/QueueList/useDropPostMutation'
import { useSwapPostsMutation } from '~publish/pages/Channel/QueueList/useSwapPostsMutation'

import { isUndatedContentItemDragData } from '../contentItemDrag'
import {
  isUndatedDraftDragData,
  isUndatedDraftsSidebarDropData,
} from '../undatedDraftDrag'
import { GetCalendarAndPostList } from './useCalendarAndPostsList'
import { updatePostScheduledTimeCache } from './updatePostScheduledTimeCache'
import { useRemovePostScheduledTime } from './useRemovePostScheduledTime'
import { useUpdatePostDueAt } from './useUpdatePostDueAt'

const FIVE_MINUTES_MS = 5 * 60 * 1000

type ContentItemTargetDateResult =
  | { success: true }
  | { success: false; error: string }

type UseHandleDragEndOptions = {
  posts: CalendarPostCard_PostData[]
  preserveTime: boolean
  timezone: string
  is24HourFormat: boolean
  onMovingToCustomSchedule?: (
    previousDueAt: string,
    newDueAt: string,
  ) => Promise<boolean>
  /**
   * Channel-less drafts currently placed in cells, used to read a dragged
   * draft's previous `targetDate` when preserving its time-of-day on reschedule.
   */
  contentItemDrafts?: ReadonlyArray<{ id: string; targetDate?: string | null }>
  updateContentItemTargetDate?: (
    id: string,
    targetDate: string,
  ) => Promise<ContentItemTargetDateResult>
}

/**
 * A custom hook that returns a function to handle the end of a drag event for calendar posts.
 *
 * @param {Object} options - The options for configuring the drag end behavior.
 * @param {Array<CalendarPostCard_PostData>} options.posts - An array of post objects, each containing fragment data from CalendarPostCard_Post.
 * @param {boolean} options.preserveTime - Determines whether to preserve the original time when rescheduling. If true, only the date will change; if false, both date and time will be updated.
 * @param {string} options.timezone - The timezone to use for date calculations and formatting.
 * @param {boolean} options.is24HourFormat - Whether to use 24-hour time format (true) or 12-hour format (false) for time display.
 * @param {Function} options.onMovingToCustomSchedule - A function that is called when a post is moved to a custom schedule.
 * @param {Array} options.contentItemDrafts - Channel-less drafts currently in cells; used to read a dragged draft's previous targetDate when preserving its time-of-day on reschedule.
 * @param {Function} options.updateContentItemTargetDate - Mutation that sets a channel-less draft's targetDate; without it, content-item drags are ignored.
 *
 * @returns {(result: DragEndEvent) => void} A function that handles the end of a drag event.
 *   This function updates the due date of a post when it's dragged to a new position in the calendar.
 *
 * @example
 * const handleDragEnd = useHandleDragEnd({
 *   posts: data?.posts.edges?.map((edge) => readFragment(CalendarPostCard_Post, edge.node)) ?? [],
 *   preserveTime: viewMode === 'month',
 *   timezone,
 *   is24HourFormat: hasTwentyFourHourTimeFormat,
 * });
 *
 * // Later in your component:
 * <Calendar
 *   // ... other props
 *   onDragEnd={handleDragEnd}
 * />
 */
export const useHandleDragEnd = ({
  posts,
  preserveTime,
  timezone = CURRENT_TIME_ZONE,
  is24HourFormat = true,
  onMovingToCustomSchedule,
  contentItemDrafts = [],
  updateContentItemTargetDate,
}: UseHandleDragEndOptions): ((result: DragEndEvent) => void) => {
  const { updatePostDueAt } = useUpdatePostDueAt()
  const [dropPost] = useDropPostMutation()
  const [swapPosts] = useSwapPostsMutation()
  const [removePostScheduledTime] = useRemovePostScheduledTime()
  const { cache } = useApolloClient()

  const handleDragEnd = useCallback(
    async ({ active, over }: DragEndEvent): Promise<void> => {
      const isSlotDrop = over?.data?.current?.type === 'slot'
      const isPostDrop = over?.data?.current?.type === 'post'
      const isUndatedDraftsSidebarDrop = isUndatedDraftsSidebarDropData(
        over?.data?.current,
      )
      const undatedDragData = isUndatedDraftDragData(active.data.current)
        ? active.data.current
        : null
      const isUndatedPostDrag = Boolean(undatedDragData)
      const isPostDrag = active.data.current?.type === 'post'
      const undatedContentItemDragData = isUndatedContentItemDragData(
        active.data.current,
      )
        ? active.data.current
        : null
      const isContentItemDrag = active.data.current?.type === 'contentItem'
      const scheduledPost = posts.find(
        (candidatePost) => candidatePost.id === active.id.toString(),
      )

      if (
        !over ||
        !active ||
        (active.data.current?.timestamp === over.data.current?.timestamp &&
          !isSlotDrop &&
          scheduledPost?.isCustomScheduled)
      ) {
        return
      }

      // Channel-less drafts reschedule via targetDate — from the No Date
      // sidebar onto a cell (schedule) or cell-to-cell (reschedule). Dropping
      // elsewhere (back on the sidebar, a slot, another post) is a no-op:
      // clearing a draft's date isn't wired here.
      if (undatedContentItemDragData || isContentItemDrag) {
        const isCellDrop =
          !isSlotDrop && !isPostDrop && !isUndatedDraftsSidebarDrop
        if (!isCellDrop || !updateContentItemTargetDate) return
        // Cell-to-cell only: a no-op drop onto the same cell. Sidebar drags have
        // no timestamp, so this must not short-circuit them.
        if (
          !undatedContentItemDragData &&
          active.data.current?.timestamp === over.data.current?.timestamp
        ) {
          return
        }

        const contentItemId = undatedContentItemDragData
          ? undatedContentItemDragData.contentItem.id
          : active.id.toString()
        const previousTargetDate = contentItemDrafts.find(
          (draft) => draft.id === contentItemId,
        )?.targetDate

        // Cell-to-cell keeps the draft's time-of-day in month view (mirroring
        // posts); a sidebar draft has no prior time, so take the cell's.
        const targetDate =
          !undatedContentItemDragData && previousTargetDate
            ? getNewTimestamp(
                previousTargetDate,
                over.data.current?.timestamp,
                preserveTime,
                timezone,
              )
            : getContentItemDropTimestamp(over.data.current)
        if (!targetDate) return

        const action = undatedContentItemDragData ? 'scheduled' : 'rescheduled'
        const result = await updateContentItemTargetDate(
          contentItemId,
          targetDate,
        )
        if (result.success) {
          toast.success(
            getContentItemSuccessMessage(
              targetDate,
              timezone,
              is24HourFormat,
              action,
            ),
          )
        } else {
          toast.error(
            `Failed to ${undatedContentItemDragData ? 'schedule' : 'reschedule'} draft, ${result.error}`,
          )
        }
        return
      }

      if (isUndatedDraftsSidebarDrop) {
        if (isUndatedPostDrag) return

        if (!isPostDrag || !scheduledPost) {
          toast.error('This element cannot be dropped here')
          return
        }

        if (!isDraftLikePost(scheduledPost)) {
          toast.error(
            'Only drafts and approvals can be moved to Undated Drafts',
          )
          return
        }

        updatePostScheduledTimeCache(cache, {
          id: scheduledPost.id,
          dueAt: null,
          status: scheduledPost.status,
          channelId: scheduledPost.channelId,
          tagIds: scheduledPost.tags.map((tag) => tag.id),
          isCustomScheduled: false,
          isPinned: false,
        })

        const removeScheduledTimeResult = await removePostScheduledTime({
          variables: { input: { id: scheduledPost.id } },
        })

        if (removeScheduledTimeResult.success) {
          toast.success(getRemoveScheduledTimeSuccessMessage(scheduledPost))
        } else {
          updatePostScheduledTimeCache(cache, {
            id: scheduledPost.id,
            dueAt: scheduledPost.dueAt,
            status: scheduledPost.status,
            channelId: scheduledPost.channelId,
            tagIds: scheduledPost.tags.map((tag) => tag.id),
            isCustomScheduled: scheduledPost.isCustomScheduled,
            isPinned: scheduledPost.isPinned,
          })

          toast.error(
            `Failed to move draft to Undated Drafts, ${removeScheduledTimeResult.error.message}`,
          )
        }
        return
      }

      if (isPostDrop && isUndatedPostDrag) {
        toast.error('Drop drafts onto a calendar time to schedule them')
        return
      }

      if ((isPostDrop && !isPostDrag) || (isSlotDrop && !isPostDrag)) {
        toast.error('This element cannot be dropped here')
        return
      }

      // Post dropped on Slot (Pin Post)
      if (isSlotDrop && isPostDrag && over.data.current?.timestamp) {
        const draggedPost = undatedDragData
          ? undatedDragData.post
          : scheduledPost
        if (!draggedPost) return

        const newSlotDate = new Date(over.data.current.timestamp).toISOString()

        const dropPostResult = await dropPost({
          variables: { input: { id: draggedPost.id, dueAt: newSlotDate } },
          ...(isUndatedPostDrag
            ? {}
            : {
                // Slot drops can shift queue-derived state beyond the moved post.
                refetchQueries: [GetPostList, GetCalendarAndPostList],
              }),
        })

        if (dropPostResult.success) {
          toast.success(
            getSuccessMessage(
              newSlotDate,
              timezone,
              draggedPost,
              is24HourFormat,
              false,
              isUndatedPostDrag ? 'scheduled' : 'rescheduled',
            ),
          )
        } else {
          toast.error(
            `Failed to reschedule post, ${dropPostResult.error.message}`,
          )
        }
      }

      // Post dropped on Post (Swap Posts)
      if (active.id !== over.id && isPostDrop && !isUndatedPostDrag) {
        const draggedPostId = active.id.toString()
        const overPostId = over.id.toString()

        const swapPostsResult = await swapPosts({
          variables: {
            input: { sourceId: draggedPostId, targetId: overPostId },
          },
        })

        if (swapPostsResult.success) {
          toast.success('Posts swapped successfully')
        } else {
          toast.error(swapPostsResult.error.message)
        }
      }

      // Post dropped on Hour or Day (Reschedule Post)
      if (!isSlotDrop && !isPostDrop) {
        if (undatedDragData) {
          const dueAtTimestamp = getUndatedPostDropTimestamp(over.data.current)
          if (!dueAtTimestamp) return

          const dueAt = new Date(dueAtTimestamp).toISOString()
          const dropPostResult = await dropPost({
            variables: {
              input: { id: undatedDragData.post.id, dueAt, isPinned: false },
            },
          })

          if (dropPostResult.success) {
            toast.success(
              getSuccessMessage(
                dueAt,
                timezone,
                undatedDragData.post,
                is24HourFormat,
                false,
                'scheduled',
              ),
            )
          } else {
            toast.error(
              `Failed to schedule draft, ${dropPostResult.error.message}`,
            )
          }
          return
        }

        if (!scheduledPost || !scheduledPost.dueAt) return

        const dueAt = getNewTimestamp(
          scheduledPost.dueAt,
          over.data.current?.timestamp,
          preserveTime,
          timezone,
        )

        const isMovingToCustomSchedule = !scheduledPost.isCustomScheduled

        if (isMovingToCustomSchedule) {
          const shouldContinue = await onMovingToCustomSchedule?.(
            scheduledPost.dueAt,
            dueAt,
          )
          if (!shouldContinue) return
        }

        try {
          await updatePostDueAt({
            id: scheduledPost.id,
            dueAt,
            isPinned: false,
          })
          toast.success(
            getSuccessMessage(
              dueAt,
              timezone,
              scheduledPost,
              is24HourFormat,
              isMovingToCustomSchedule,
            ),
          )
        } catch (error) {
          toast.error(getErrorMessage(error))
        }
      }
    },
    [
      dropPost,
      swapPosts,
      removePostScheduledTime,
      cache,
      posts,
      preserveTime,
      onMovingToCustomSchedule,
      updatePostDueAt,
      timezone,
      is24HourFormat,
      contentItemDrafts,
      updateContentItemTargetDate,
    ],
  )

  return handleDragEnd
}

type DropData = NonNullable<DragEndEvent['over']>['data']['current']

function getUndatedPostDropTimestamp(dropData: DropData): number | undefined {
  if (typeof dropData?.creationTimestamp === 'number') {
    return dropData.creationTimestamp
  }
  if (typeof dropData?.timestamp === 'number') {
    return dropData.timestamp
  }
  return undefined
}

export function getContentItemDropTimestamp(
  dropData: DropData,
): string | undefined {
  const milliseconds = getUndatedPostDropTimestamp(dropData)
  return milliseconds != null ? new Date(milliseconds).toISOString() : undefined
}

export function getContentItemSuccessMessage(
  targetDate: string,
  timezone: string,
  is24HourFormat: boolean,
  action: 'scheduled' | 'rescheduled',
): string {
  const instant = toTemporalInstant.call(new Date(targetDate))
  const timeZonedDate = instant.toZonedDateTimeISO(timezone)
  const formattedTime = format(
    timeZonedDate,
    `eee, MMMM d 'at' ${is24HourFormat ? 'HH:mm' : 'h:mm a'}`,
  )
  return `Great! We’ve ${action} your draft for ${formattedTime}`
}

export function getNewTimestamp(
  previousDueAt: string,
  toDateMilliseconds: number,
  preserveTime: boolean,
  timezone: string,
): string {
  if (!preserveTime) {
    return new Date(toDateMilliseconds).toISOString()
  }

  const fromDate =
    Temporal.Instant.from(previousDueAt).toZonedDateTimeISO(timezone)
  const toDateDate =
    Temporal.Instant.fromEpochMilliseconds(
      toDateMilliseconds,
    ).toZonedDateTimeISO(timezone)

  const newDateTime = fromDate.with({
    year: toDateDate.year,
    month: toDateDate.month,
    day: toDateDate.day,
  })

  const now = getZonedNow(newDateTime.timeZoneId)

  if (isBefore(newDateTime, now)) {
    return new Date(
      now.toInstant().epochMilliseconds + FIVE_MINUTES_MS,
    ).toISOString()
  }

  return new Date(newDateTime.toInstant().epochMilliseconds).toISOString()
}

function getErrorMessage(error: unknown): string {
  const fallbackErrorMessage =
    'Whoops, we had some problems updating the date for that post!'
  if (error instanceof Error && error.message) {
    return error.message
  }
  return fallbackErrorMessage
}

function isDraftLikePost(post: CalendarPostCard_PostData): boolean {
  return [...postStatusByTab.drafts, ...postStatusByTab.approvals].includes(
    post.status,
  )
}

function getRemoveScheduledTimeSuccessMessage(
  post: CalendarPostCard_PostData,
): string {
  const type = postStatusByTab.approvals.includes(post.status)
    ? 'approval'
    : 'draft'
  return `Moved ${type} to Undated Drafts`
}

function getSuccessMessage(
  dueAt: string,
  timezone: string,
  post: CalendarPostCard_PostData,
  is24HourFormat: boolean,
  isMovingToCustomSchedule = false,
  action: 'scheduled' | 'rescheduled' = 'rescheduled',
): string {
  const date = new Date(dueAt)
  const instant = toTemporalInstant.call(date)
  const timeZonedDate = instant.toZonedDateTimeISO(timezone)
  const type = ['draft', 'needs_approval'].includes(post.status)
    ? 'draft'
    : 'post'
  const formattedTime = format(
    timeZonedDate,
    `eee, MMMM d 'at' ${is24HourFormat ? 'HH:mm' : 'h:mm a'}`,
  )
  const message = `Great! We’ve ${action} your ${type} for ${formattedTime}`
  if (isMovingToCustomSchedule) {
    return `${message}. Queue will be recalculated shortly.`
  }
  return message
}
